import { createContext, useContext, useEffect } from 'react';
import type { AvailableLanguage } from '~/services/i18n';
import { languageStore } from '~/services/i18n-store';
import { preloadTranslations, initializeTranslations } from '~/services/i18n';

interface LanguageContextType {
    language: AvailableLanguage;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ 
    children, 
    language,
    translations = {}
}: { 
    children: React.ReactNode; 
    language: AvailableLanguage;
    translations?: Record<string, string>;
}) {
    // Set the language in the store immediately during render
    // This ensures the store is initialized before any child components render
    // and prevents hydration mismatches
    languageStore.setLanguage(language);
    
    // Initialize translations immediately during render if provided from server
    if (language !== 'en' && Object.keys(translations).length > 0) {
        // Directly set the translations in cache
        initializeTranslations(language, translations);
    }
    
    // Fallback: preload translations on the client side if not already provided
    useEffect(() => {
        if (language !== 'en' && Object.keys(translations).length === 0) {
            preloadTranslations(language).catch(() => {
                // Silently handle errors
            });
        }
    }, [language, translations]);
    
    return (
        <LanguageContext.Provider value={{ language }}>
            {children}
        </LanguageContext.Provider>
    );
}

export function useLanguage() {
    const context = useContext(LanguageContext);
    // Return default if context is not available (for components outside provider)
    return context?.language || 'en';
}